import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AuditTrailViewer = ({ auditLogs, onExport }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [dateRange, setDateRange] = useState('today');

  const actionOptions = [
    { value: 'all', label: 'جميع الإجراءات' },
    { value: 'login', label: 'تسجيل الدخول' },
    { value: 'logout', label: 'تسجيل الخروج' },
    { value: 'create', label: 'إنشاء' },
    { value: 'update', label: 'تحديث' },
    { value: 'delete', label: 'حذف' },
    { value: 'export', label: 'تصدير' },
  ];

  const dateRangeOptions = [
    { value: 'today', label: 'اليوم' },
    { value: 'week', label: 'آخر أسبوع' },
    { value: 'month', label: 'آخر شهر' },
    { value: 'all', label: 'جميع الفترات' },
  ];

  const actionIcons = {
    login: 'LogIn',
    logout: 'LogOut',
    create: 'Plus',
    update: 'Edit',
    delete: 'Trash2',
    export: 'Download',
  };

  const actionColors = {
    login: 'text-[var(--color-success)]',
    logout: 'text-[var(--color-muted-foreground)]',
    create: 'text-[var(--color-primary)]',
    update: 'text-[var(--color-accent)]',
    delete: 'text-[var(--color-error)]',
    export: 'text-[var(--color-secondary)]',
  };

  const filteredLogs = auditLogs?.filter(log => {
    const matchesSearch = log?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.userName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesAction = filterAction === 'all' || log?.action === filterAction;
    const matchesUser = filterUser === 'all' || log?.userId === filterUser;
    return matchesSearch && matchesAction && matchesUser;
  });

  const uniqueUsers = [...new Set(auditLogs.map(log => log.userId))];
  const userOptions = [
    { value: 'all', label: 'جميع المستخدمين' },
    ...uniqueUsers?.map(userId => {
      const user = auditLogs?.find(log => log?.userId === userId);
      return { value: userId, label: user?.userName };
    })
  ];

  return (
    <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] shadow-warm">
      <div className="p-4 md:p-6 border-b border-[var(--color-border)]">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-[var(--color-foreground)]">
              سجل الأنشطة
            </h3>
            <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
              عرض جميع العمليات والأنشطة في النظام
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={onExport}
          >
            تصدير السجل
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input
            type="search"
            placeholder="البحث في السجل..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
          />
          <Select
            options={actionOptions}
            value={filterAction}
            onChange={setFilterAction}
            placeholder="نوع الإجراء"
          />
          <Select
            options={userOptions}
            value={filterUser}
            onChange={setFilterUser}
            placeholder="المستخدم"
          />
          <Select
            options={dateRangeOptions}
            value={dateRange}
            onChange={setDateRange}
            placeholder="الفترة الزمنية"
          />
        </div>
      </div>
      <div className="p-4 md:p-6">
        <div className="space-y-3">
          {filteredLogs?.map((log) => (
            <div
              key={log?.id}
              className="flex items-start gap-4 p-4 bg-[var(--color-background)] rounded-lg border border-[var(--color-border)] hover:shadow-warm transition-shadow"
            >
              <div className={`p-2 rounded-lg bg-[var(--color-muted)] ${actionColors?.[log?.action]}`}>
                <Icon name={actionIcons?.[log?.action]} size={20} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex items-center gap-3">
                    <Image
                      src={log?.userAvatar}
                      alt={log?.userAvatarAlt}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">{log?.userName}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">{log?.userRole}</p>
                    </div>
                  </div>
                  <span className="text-xs text-[var(--color-muted-foreground)] whitespace-nowrap">
                    {log?.timestamp}
                  </span>
                </div>

                <p className="text-sm text-[var(--color-foreground)] mb-2">{log?.description}</p>

                {log?.details && (
                  <div className="flex flex-wrap items-center gap-2">
                    {Object.entries(log?.details)?.map(([key, value]) => (
                      <span
                        key={key}
                        className="px-2 py-1 bg-[var(--color-muted)] rounded text-xs text-[var(--color-muted-foreground)]"
                      >
                        {key}: {value}
                      </span>
                    ))}
                  </div>
                )}

                {log?.ipAddress && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-[var(--color-muted-foreground)]">
                    <Icon name="Globe" size={12} />
                    <span>IP: {log?.ipAddress}</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {filteredLogs?.length === 0 && (
            <div className="text-center py-12">
              <Icon name="FileSearch" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
              <p className="text-[var(--color-muted-foreground)]">لا توجد سجلات تطابق معايير البحث</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditTrailViewer;